/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState, useCallback } from "react";
import "./CompanyModal.css";
import { CompanyModalContent, CompanyModalImage } from "../../types";
import { getCustomElementUrl } from "../../utils/cloudinary";
import { stringToCompanyModalImage } from "../../services/companyModalImageService";
import { companyLogoToPicture } from "../../services/companyLogoService";
import Tooltip from "../tooltip/Tooltip";

// Cloudinary URLs for images
const closeIcon = getCustomElementUrl('cross');
const internetIcon = getCustomElementUrl('internet_icon');
const arrowDown = getCustomElementUrl('arrow_down');

interface CompanyModalProps {
    company: CompanyModalContent;
    onClose: () => void;
    onImageClick: (
        imageSrc: string,
        objectFit: "cover" | "contain" | "fill" | "scale-down" | "none",
        descriptiveTitle?: string
    ) => void;
    inFocus: boolean;
    isVisible?: boolean;
}

const getDomainFromUrl = (url: string) => {
    try {
        const domain = new URL(url).hostname.replace("www.", "");
        return domain.charAt(0).toUpperCase() + domain.slice(1);
    } catch {
        return url.charAt(0).toUpperCase() + url.slice(1);
    }
};

const CompanyModal = ({
    company,
    onClose,
    onImageClick,
    inFocus,
    isVisible = true,
}: CompanyModalProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const rightContainerRef = useRef<HTMLDivElement>(null);
    const fadeTimeoutRef = useRef<NodeJS.Timeout>();

    // Keep track of which actual index is intended:
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    // Track window width
    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1200);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // Update to store both path and objectFit
    const [displayedImage, setDisplayedImage] = useState<CompanyModalImage>({
        path: stringToCompanyModalImage(company.images[0].path) || "",
        objectFit: company.images[0].objectFit,
    });

    // Whether we are in the middle of a fade-out/fade-in:
    const [isFading, setIsFading] = useState(false);

    // Add tooltip states
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

    // Add these state variables
    const [hasScrolled, setHasScrolled] = useState(false);
    const [isScrollable, setIsScrollable] = useState(false);
    const [isHoveringImage, setIsHoveringImage] = useState(false);

    // Add a ref to track the latest intended image index
    const intendedIndexRef = useRef(currentImageIndex);
    // Add ref to store the latest mouse position
    const lastMousePositionRef = useRef({ x: 0, y: 0 });

    // Clear any pending fade timeouts when unmounting
    useEffect(() => {
        return () => {
            if (fadeTimeoutRef.current) {
                clearTimeout(fadeTimeoutRef.current);
            }
        };
    }, []);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            const newIsDesktop = window.innerWidth > 1200;
            const newIsMobile = window.innerWidth <= 768;
            setIsDesktop(newIsDesktop);
            setIsMobile(newIsMobile);

            // If switching to mobile, reset to first image
            if (!newIsDesktop) {
                setCurrentImageIndex(0);
                setDisplayedImage(
                    company.images.length
                        ? {
                              path:
                                  stringToCompanyModalImage(
                                      company.images[0].path
                                  ) || "",
                              objectFit: company.images[0].objectFit,
                          }
                        : { path: "", objectFit: "cover" }
                );
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [company.images]);

    // Scroll-based image change for desktop
    useEffect(() => {
        const rightContainer = rightContainerRef.current;
        if (!rightContainer || !isDesktop) return;

        // Check if content is scrollable
        const checkIfScrollable = () => {
            if (rightContainer) {
                setIsScrollable(
                    rightContainer.scrollHeight >
                        rightContainer.clientHeight + 20
                );
            }
        };

        checkIfScrollable();
        window.addEventListener("resize", checkIfScrollable);

        // Direct scroll handler without throttling, like in ProjectModal
        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = rightContainer;

            // Set hasScrolled state - works on both mobile and desktop
            if (scrollTop > 10) {
                setHasScrolled(true);
            } else {
                setHasScrolled(false);
            }

            const totalImages = company.images.length;
            if (totalImages <= 1) return;

            // Limit to fewer images for Tesla and SpaceX on iOS
            const isIOS =
                /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                (navigator.platform === "MacIntel" &&
                    navigator.maxTouchPoints > 1);

            const effectiveNumImages = Math.min(
                totalImages,
                company.title === "Tesla" || company.title === "SpaceX"
                    ? isIOS
                        ? 6
                        : 20 // Limit to 6 on iOS, 20 on other platforms
                    : isIOS
                    ? 3
                    : 6 // Limit to 3 on iOS, 6 on other platforms
            );

            // Calculate scroll percentage and map it to image indices
            const maxScroll = scrollHeight - clientHeight;
            const scrollPercentage = Math.max(
                0,
                Math.min(1, scrollTop / maxScroll)
            );
            const imageIndex = Math.min(
                Math.floor(scrollPercentage * effectiveNumImages),
                effectiveNumImages - 1
            );

            if (imageIndex !== currentImageIndex) {
                setCurrentImageIndex(imageIndex);
            }
        };

        // Use passive event listener for better performance
        rightContainer.addEventListener("scroll", handleScroll);
        return () => {
            rightContainer.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", checkIfScrollable);
        };
    }, [company.images.length, isDesktop, currentImageIndex, company.title]);

    // Update the image transition effect with preloading
    useEffect(() => {
        if (!isDesktop) return;

        // Check if we're on iOS
        const isIOS =
            /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

        // Update the intended index ref immediately
        intendedIndexRef.current = currentImageIndex;

        // Ensure the index is valid
        if (currentImageIndex >= company.images.length) {
            return;
        }

        const newImage: CompanyModalImage = {
            path:
                stringToCompanyModalImage(
                    company.images[currentImageIndex].path
                ) || "",
            objectFit: company.images[currentImageIndex].objectFit,
        };

        if (newImage.path === displayedImage.path) return;

        // Clear any existing timeout
        if (fadeTimeoutRef.current) {
            clearTimeout(fadeTimeoutRef.current);
            fadeTimeoutRef.current = undefined;
        }

        // On iOS, use a simpler transition approach to reduce memory usage
        if (isIOS) {
            setIsFading(true);
            fadeTimeoutRef.current = setTimeout(() => {
                setDisplayedImage(newImage);
                setIsFading(false);
            }, 300);
            return;
        }

        // For non-iOS, use the image preloading approach
        const imgLoader = new Image();
        setIsFading(true);

        imgLoader.onload = () => {
            if (intendedIndexRef.current === currentImageIndex) {
                fadeTimeoutRef.current = setTimeout(() => {
                    if (intendedIndexRef.current === currentImageIndex) {
                        setDisplayedImage(newImage);
                        setIsFading(false);
                    }
                }, 300);
            }
        };

        imgLoader.onerror = () => {
            // Use a safer way to log errors on iOS
            if (window.navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
                // Create a hidden div to display errors if needed
                const errorDiv =
                    document.getElementById("ios-error-log") ||
                    (() => {
                        const div = document.createElement("div");
                        div.id = "ios-error-log";
                        div.style.display = "none";
                        document.body.appendChild(div);
                        return div;
                    })();

                errorDiv.setAttribute(
                    "data-last-error",
                    `Failed to load image: ${newImage.path}`
                );
            }

            if (intendedIndexRef.current === currentImageIndex) {
                fadeTimeoutRef.current = setTimeout(() => {
                    if (intendedIndexRef.current === currentImageIndex) {
                        setDisplayedImage(newImage);
                        setIsFading(false);
                    }
                }, 300);
            }
        };

        // Add error handling for image loading
        try {
            imgLoader.src = newImage.path;
        } catch (error) {
            // Fallback if image loading fails
            setIsFading(false);
            // Log error safely
            if (window.navigator.userAgent.match(/iPhone|iPad|iPod/i)) {
                const errorDiv =
                    document.getElementById("ios-error-log") ||
                    document.createElement("div");
                errorDiv.id = "ios-error-log";
                errorDiv.style.display = "none";
                errorDiv.setAttribute("data-error", String(error));
                if (!errorDiv.parentNode) document.body.appendChild(errorDiv);
            }
        }

        // Cleanup function to handle component unmount or rapid changes
        return () => {
            if (fadeTimeoutRef.current) {
                clearTimeout(fadeTimeoutRef.current);
                fadeTimeoutRef.current = undefined;
            }
        };
    }, [currentImageIndex, company.images, isDesktop, displayedImage.path]);

    // Preload all company images on component mount
    useEffect(() => {
        if (!isDesktop || company.images.length <= 1) return;

        // For iOS, limit the number of preloaded images to prevent memory issues
        const isIOS =
            /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

        // Only preload a limited number of images on iOS
        const imagesToPreload = isIOS
            ? company.images.slice(0, Math.min(3, company.images.length))
            : company.images;

        // Preload selected company images
        imagesToPreload.forEach((img) => {
            const imgPath = stringToCompanyModalImage(img.path);
            if (imgPath) {
                const preloadImage = new Image();
                preloadImage.src = imgPath;
            }
        });
    }, [company.images, isDesktop]);

    // Track mouse to position the tooltip
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const position = { x: e.clientX, y: e.clientY + 20 };
        lastMousePositionRef.current = position;
        setTooltipPosition(position);
    };

    // Add effect to manage body scroll
    useEffect(() => {
        // Add modal-open class to body when modal opens
        document.body.classList.add("modal-open");

        // Cleanup function when component unmounts
        return () => {
            // Remove modal-open class when modal closes
            document.body.classList.remove("modal-open");
        };
    }, []);

    // Update tooltip visibility when isVisible changes and cursor is already over image
    useEffect(() => {
        if (isVisible && isHoveringImage && !isMobile) {
            // Get current mouse position from lastMousePositionRef or use window's mouse position
            if (
                lastMousePositionRef.current.x === 0 &&
                lastMousePositionRef.current.y === 0 &&
                typeof window !== "undefined"
            ) {
                // Try to get the current mouse position from the global MouseEvent
                const mouseEvent = window.event as MouseEvent | undefined;
                if (mouseEvent) {
                    const position = {
                        x: mouseEvent.clientX,
                        y: mouseEvent.clientY + 20,
                    };
                    setTooltipPosition(position);
                } else {
                    setTooltipPosition(lastMousePositionRef.current);
                }
            } else {
                setTooltipPosition(lastMousePositionRef.current);
            }

            setShowTooltip(true);
        } else if (!isVisible && showTooltip) {
            // Ensure tooltip is hidden when component is not visible
            setShowTooltip(false);
        }
    }, [isVisible, isHoveringImage, isMobile]);

    // This function will be called when the component becomes visible with mouse already over it
    const detectMousePosition = useCallback(() => {
        // Use position from mouse move event listener
        const updatePosition = (e: MouseEvent) => {
            const position = {
                x: e.clientX,
                y: e.clientY + 20,
            };
            lastMousePositionRef.current = position;
            setTooltipPosition(position);
            // Remove the listener since we only need it once
            window.removeEventListener("mousemove", updatePosition);
        };

        // Listen for a single mouse move to get position
        window.addEventListener("mousemove", updatePosition, { once: true });
    }, []);

    // When isVisible changes to true, try to detect mouse position
    useEffect(() => {
        if (isVisible && isHoveringImage) {
            detectMousePosition();
        }
    }, [isVisible, isHoveringImage, detectMousePosition]);

    return (
        <div className="company-modal-outer-container" ref={containerRef}>
            <div className="company-modal-content-container">
                <div className="company-modal-x-button-container">
                    <div
                        className="company-modal-x-button-outline"
                        onClick={onClose}
                    >
                        <div className="company-modal-x-button-inner">
                            <img
                                src={closeIcon}
                                alt="Close"
                                className="company-modal-x-button-icon"
                            />
                        </div>
                    </div>
                </div>
                <div className="company-modal-left-container">
                    <div
                        className="company-modal-image-container"
                        onMouseEnter={(e) => {
                            setIsHoveringImage(true);
                            const position = {
                                x: e.clientX,
                                y: e.clientY + 20,
                            };
                            lastMousePositionRef.current = position;
                            setTooltipPosition(position);
                            if (!isMobile && isVisible) {
                                setShowTooltip(true);
                            }
                        }}
                        onMouseLeave={() => {
                            setIsHoveringImage(false);
                            setShowTooltip(false);
                        }}
                        onMouseMove={handleMouseMove}
                        onTouchMove={(e) => {
                            // Don't prevent default to allow clicks to work
                            // Only handle the tooltip position
                            if (e.touches && e.touches.length > 0) {
                                const position = {
                                    x: e.touches[0].clientX,
                                    y: e.touches[0].clientY - 40,
                                };
                                lastMousePositionRef.current = position;
                                setTooltipPosition(position);
                            }
                        }}
                    >
                        <img
                            className={
                                "company-modal-left-container-image " +
                                (isFading ? "fade-out" : "fade-in")
                            }
                            src={displayedImage.path}
                            alt={company.title}
                            style={{ objectFit: displayedImage.objectFit }}
                            onClick={() =>
                                inFocus &&
                                onImageClick(
                                    displayedImage.path,
                                    displayedImage.objectFit
                                )
                            }
                            role="button"
                        />
                        {showTooltip && isVisible && (
                            <Tooltip
                                text="Click to expand picture"
                                position={tooltipPosition}
                            />
                        )}
                    </div>
                </div>
                <div
                    className="company-modal-right-container"
                    ref={rightContainerRef}
                >
                    <div className="company-modal-right-container-title-and-text-container">
                        <div className="company-modal-right-container-logo-and-title-container">
                            <img
                                src={companyLogoToPicture(company.logo)}
                                alt={company.title}
                                className="company-modal-right-container-logo"
                            />
                            <div className="company-modal-right-container-title">
                                {company.title}
                            </div>
                        </div>
                        {/* Link to the company's website */}
                        <div className="company-modal-right-container-website-link-container">
                            <div className="company-modal-right-container-internet-icon-container">
                                <img
                                    src={internetIcon}
                                    alt="Internet"
                                    className="company-modal-right-container-internet-icon"
                                />
                            </div>
                            <div className="company-modal-right-container-website-link">
                                <a
                                    href={company.websiteLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {getDomainFromUrl(company.websiteLink)}
                                </a>
                            </div>
                        </div>
                        <div className="company-modal-right-container-text">
                            {company.paragraphs.map((paragraph, index) => (
                                <React.Fragment key={index}>
                                    {typeof paragraph === "string"
                                        ? // Render simple text paragraph
                                          paragraph
                                        : // Render rich paragraph with links
                                          paragraph.segments.map(
                                              (segment, segmentIndex) => (
                                                  <React.Fragment
                                                      key={segmentIndex}
                                                  >
                                                      {segment.type ===
                                                      "text" ? (
                                                          segment.bold ? (
                                                              <strong>
                                                                  {
                                                                      segment.content
                                                                  }
                                                              </strong>
                                                          ) : (
                                                              segment.content
                                                          )
                                                      ) : (
                                                          <a
                                                              href={
                                                                  segment.link
                                                                      ?.url
                                                              }
                                                              target={
                                                                  segment.link
                                                                      ?.newTab !==
                                                                  false
                                                                      ? "_blank"
                                                                      : "_self"
                                                              }
                                                              rel="noopener noreferrer"
                                                              style={
                                                                  segment.bold
                                                                      ? {
                                                                            fontWeight:
                                                                                "bold",
                                                                        }
                                                                      : undefined
                                                              }
                                                          >
                                                              {segment.content}
                                                          </a>
                                                      )}
                                                  </React.Fragment>
                                              )
                                          )}
                                    <br />
                                    <br />
                                </React.Fragment>
                            ))}
                            {!hasScrolled && isScrollable && (
                                <div className="company-modal-scroll-down-button">
                                    <p>You can scroll</p>
                                    <div className="scroll-down-arrow-container">
                                        <img
                                            src={arrowDown}
                                            alt="Scroll down"
                                            className="scroll-down-arrow"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompanyModal;
